import React, { useCallback } from "react";
import { Play, ChevronLeft, ChevronRight } from "lucide-react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { SectionHeading } from "./SectionHeading";

const TESTIMONIALS_API_URL = "https://api.galleriadentalgrp.com/api/public/testimonials";
const TESTIMONIALS_API_TOKEN =
  process.env.REACT_APP_TESTIMONIALS_API_TOKEN || "gd_23ec6c2817c94efb85ac1308d47b4a8c";

const getFirstString = (item, keys) => {
  for (const key of keys) {
    const value = item?.[key];
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }
  return "";
};

const normalizeVideoTestimonial = (item, index) => {
  const location = getFirstString(item, ["location", "city", "subtitle", "category"]);
  return {
    id: item?._id || item?.id || `video-testimonial-${index}`,
    name: getFirstString(item, ["patientName", "name", "label", "title"]) || `Video ${index + 1}`,
    location: location ? location.replace(/[-_]/g, " ") : "Patient testimonial",
    description: getFirstString(item, ["description", "text", "quote"]),
    videoUrl: getFirstString(item, ["video", "videoUrl", "video_url", "url", "mediaUrl"]),
  };
};

const preparePreviewFrame = (videoElement) => {
  if (!videoElement || videoElement.dataset.previewReady === "true") {
    return;
  }
  const seekToPreviewFrame = () => {
    videoElement.dataset.previewReady = "true";
    try {
      videoElement.currentTime = 0.1;
    } catch {
      videoElement.dataset.previewReady = "failed";
    }
  };
  if (videoElement.readyState >= 2) {
    seekToPreviewFrame();
    return;
  }
  videoElement.addEventListener("loadeddata", seekToPreviewFrame, { once: true });
};

const VideoTestimonialsCarousel = ({ videos, loading, error }) => {
  const autoplay = React.useRef(Autoplay({ delay: 4000, stopOnInteraction: false }));
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, align: "start", slidesToScroll: 1, dragFree: true },
    [autoplay.current],
  );
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const [activeVideo, setActiveVideo] = React.useState(null);
  const hasVideos = videos.length > 0;
  const activeVideoIndex = activeVideo ? videos.findIndex((video) => video.id === activeVideo.id) : -1;

  const showPreviousVideo = useCallback(() => {
    if (activeVideoIndex < 0 || videos.length < 2) return;
    setActiveVideo(videos[(activeVideoIndex - 1 + videos.length) % videos.length]);
  }, [activeVideoIndex, videos]);

  const showNextVideo = useCallback(() => {
    if (activeVideoIndex < 0 || videos.length < 2) return;
    setActiveVideo(videos[(activeVideoIndex + 1) % videos.length]);
  }, [activeVideoIndex, videos]);

  React.useEffect(() => {
    if (!emblaApi || !hasVideos) return;
    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    onSelect();
    return () => emblaApi.off("select", onSelect);
  }, [emblaApi, hasVideos]);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  React.useEffect(() => {
    if (!activeVideo) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setActiveVideo(null);
      if (event.key === "ArrowLeft") showPreviousVideo();
      if (event.key === "ArrowRight") showNextVideo();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeVideo, showNextVideo, showPreviousVideo]);

  return (
    <div className="relative select-none">
      {error && (
        <p className="mb-8 text-sm tracking-[0.2em] uppercase text-white/45">{error}</p>
      )}

      {loading && !hasVideos ? (
        <div className="py-12 flex flex-col items-center justify-center gap-4">
          <div className="w-10 h-10 rounded-full border-2 border-white/25 border-t-gold animate-spin" />
          <p className="text-[11px] tracking-[0.3em] uppercase text-white/45 font-mono">
            Loading Testimonials
          </p>
        </div>
      ) : !hasVideos ? null : (
        <>
          {/* Film strip wrapper */}
          <div className="bg-[#1a1a1a] rounded-sm overflow-hidden shadow-2xl border border-white/5">
            {/* Top sprocket strip */}
            <div className="flex items-center bg-[#111] h-8 overflow-hidden">
              {[...Array(40)].map((_, i) => (
                <div key={i} className="flex-shrink-0 w-8 flex justify-center">
                  <div className="w-5 h-4 bg-[#1a1a1a] rounded-sm border border-white/10" />
                </div>
              ))}
            </div>

            <div className="flex items-stretch">
              {/* Left holes */}
              <div className="flex-shrink-0 w-8 bg-[#111] flex flex-col justify-around py-3 gap-2 min-h-full">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="mx-auto w-4 h-3 bg-[#1a1a1a] rounded-sm border border-white/10" />
                ))}
              </div>

              {/* Slides */}
              <div className="flex-1 overflow-hidden px-1 py-3 md:px-2 md:py-4" ref={emblaRef}>
                <div className="flex gap-3">
                  {videos.map((v, i) => (
                    <div
                      key={v.id}
                      className={`group flex-[0_0_100%] md:flex-none md:w-[300px] lg:w-[340px] ${v.videoUrl ? "cursor-pointer" : "cursor-default"}`}
                      data-testid={`video-testimonial-${i}`}
                      onClick={() => v.videoUrl && setActiveVideo(v)}
                      role={v.videoUrl ? "button" : undefined}
                      tabIndex={v.videoUrl ? 0 : -1}
                      onKeyDown={(event) => {
                        if (!v.videoUrl) return;
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          setActiveVideo(v);
                        }
                      }}
                    >
                      <div className="bg-[#111] px-3 py-1 flex items-center justify-between">
                        <span className="text-[9px] tracking-[0.3em] uppercase text-white/30 font-mono">
                          VIDEO {String(i + 1).padStart(2, "0")}
                        </span>
                      </div>
                      <div className="relative aspect-[4/3] overflow-hidden border-x border-white/5 bg-black">
                        <video
                          ref={preparePreviewFrame}
                          src={v.videoUrl}
                          muted
                          playsInline
                          preload="metadata"
                          className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1500ms] group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/75" />
                        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/5" />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div
                            className={`w-14 h-14 border flex items-center justify-center transition-all duration-300 backdrop-blur-sm ${
                              v.videoUrl
                                ? "border-gold/70 group-hover:bg-gold group-hover:border-gold bg-ink/20"
                                : "border-white/15 bg-ink/40"
                            }`}
                          >
                            <Play
                              size={18}
                              strokeWidth={1.2}
                              className={`${v.videoUrl ? "text-gold group-hover:text-ink" : "text-white/40"} ml-0.5`}
                              fill="currentColor"
                            />
                          </div>
                        </div>
                        <div className="absolute inset-x-0 top-6 text-center px-6">
                          <div className="text-[10px] tracking-[0.35em] uppercase text-white/30 font-mono">
                            Patient Story
                          </div>
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 p-4">
                          <div className="text-xs tracking-[0.25em] uppercase text-white font-medium">{v.name}</div>
                          <div className="text-[10px] tracking-widest uppercase text-white/50 mt-0.5">{v.location}</div>
                        </div>
                      </div>
                      <div className="bg-[#111] px-3 py-1">
                        <div className="w-full h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                        {v.description && (
                          <div className="pt-2 text-[10px] tracking-[0.16em] uppercase text-white/35 truncate">
                            {v.description}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right holes */}
              <div className="flex-shrink-0 w-8 bg-[#111] flex flex-col justify-around py-3 gap-2 min-h-full">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="mx-auto w-4 h-3 bg-[#1a1a1a] rounded-sm border border-white/10" />
                ))}
              </div>
            </div>

            {/* Bottom sprocket strip */}
            <div className="flex items-center bg-[#111] h-8 overflow-hidden">
              {[...Array(40)].map((_, i) => (
                <div key={i} className="flex-shrink-0 w-8 flex justify-center">
                  <div className="w-5 h-4 bg-[#1a1a1a] rounded-sm border border-white/10" />
                </div>
              ))}
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center justify-center gap-4 mt-8">
            <button
              onClick={scrollPrev}
              className="w-11 h-11 border border-white/20 flex items-center justify-center hover:border-gold hover:text-gold transition-colors text-white"
              aria-label="Previous"
            >
              <ChevronLeft size={18} strokeWidth={1.5} />
            </button>
            <span className="text-[10px] tracking-[0.35em] uppercase text-white/30 font-mono">drag or click</span>
            <button
              onClick={scrollNext}
              className="w-11 h-11 border border-white/20 flex items-center justify-center hover:border-gold hover:text-gold transition-colors text-white"
              aria-label="Next"
            >
              <ChevronRight size={18} strokeWidth={1.5} />
            </button>
          </div>

          {/* Count indicator */}
          <div className="flex items-center justify-center gap-3 mt-5">
            {videos.map((_, i) => (
              <button
                key={i}
                onClick={() => emblaApi?.scrollTo(i)}
                className={`transition-all duration-300 rounded-full ${
                  i === selectedIndex % videos.length
                    ? "w-6 h-1.5 bg-gold"
                    : "w-1.5 h-1.5 bg-white/20 hover:bg-white/40"
                }`}
                aria-label={`Go to video ${i + 1}`}
              />
            ))}
            <span className="ml-2 text-[10px] tracking-[0.3em] uppercase text-white/30 font-mono">
              {loading ? "loading" : `${(selectedIndex % videos.length) + 1} / ${videos.length}`}
            </span>
          </div>
        </>
      )}

      {activeVideo && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 px-4 py-6 md:px-8"
          role="dialog"
          aria-modal="true"
          aria-label={activeVideo.name}
          onClick={() => setActiveVideo(null)}
        >
          <div
            className="relative w-full max-w-5xl border border-white/10 bg-[#111] shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-4 px-4 py-3 border-b border-white/10 text-white">
              <div className="min-w-0">
                <div className="text-xs tracking-[0.25em] uppercase text-gold">Now Playing</div>
                <div className="mt-1 text-lg md:text-xl">{activeVideo.name}</div>
                <div className="text-xs tracking-[0.2em] uppercase text-white/45 mt-1">{activeVideo.location}</div>
                <div className="mt-1 text-[10px] tracking-[0.2em] uppercase text-white/30">
                  {activeVideoIndex + 1} / {videos.length}
                </div>
              </div>
              <div className="flex flex-shrink-0 items-center gap-2">
                <button
                  type="button"
                  className="border border-white/15 px-4 py-2 text-xs tracking-[0.2em] uppercase text-white/70 transition-colors hover:border-gold hover:text-gold"
                  onClick={() => setActiveVideo(null)}
                >
                  Close
                </button>
              </div>
            </div>
            {videos.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={showPreviousVideo}
                  className="absolute left-3 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center border border-white/25 bg-black/65 text-white transition-colors hover:border-gold hover:text-gold"
                  aria-label="Previous video testimonial"
                >
                  <ChevronLeft size={22} strokeWidth={1.4} />
                </button>
                <button
                  type="button"
                  onClick={showNextVideo}
                  className="absolute right-3 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center border border-white/25 bg-black/65 text-white transition-colors hover:border-gold hover:text-gold"
                  aria-label="Next video testimonial"
                >
                  <ChevronRight size={22} strokeWidth={1.4} />
                </button>
              </>
            )}
            <div className="bg-black">
              <video
                src={activeVideo.videoUrl}
                controls
                autoPlay
                playsInline
                className="w-full max-h-[75vh] bg-black"
              >
                Your browser does not support embedded video playback.
              </video>
            </div>
            {activeVideo.description && (
              <div className="px-4 py-3 text-sm text-white/70 border-t border-white/10">
                {activeVideo.description}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const VideoTestimonialsSection = () => {
  const [videos, setVideos] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    const controller = new AbortController();

    const load = async () => {
      setLoading(true);
      setError("");

      try {
        const params = new URLSearchParams({
          limit: "12",
          category: "testimonial",
          sort: "createdAt",
          order: "asc",
        });
        const response = await fetch(`${TESTIMONIALS_API_URL}?${params.toString()}`, {
          signal: controller.signal,
          headers: { Authorization: `Bearer ${TESTIMONIALS_API_TOKEN}` },
        });
        const payload = await response.json();

        if (!response.ok || !payload?.success) {
          throw new Error(payload?.message || "Unable to load testimonials.");
        }

        const rawVideos = Array.isArray(payload?.data) ? payload.data : [];
        const liveVideos = rawVideos
          .slice()
          .sort((a, b) => {
            const aTime = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
            const bTime = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
            return aTime - bTime;
          })
          .map((item, index) => normalizeVideoTestimonial(item, index))
          .filter((item) => item.videoUrl);

        if (!liveVideos.length) {
          throw new Error("No published video testimonials were returned.");
        }

        setVideos(liveVideos);
      } catch (err) {
        if (err.name === "AbortError") return;
        setVideos([]);
        setError("");
      } finally {
        setLoading(false);
      }
    };

    load();
    return () => controller.abort();
  }, []);

  if (!loading && !videos.length && !error) return null;

  return (
    <section className="py-1 bg-ink text-bone">
      <div className="container-x">
        <SectionHeading eyebrow="In Their Words" title="Video" italicWord="testimonials." light />
        <div className="">
          <VideoTestimonialsCarousel videos={videos} loading={loading} error={error} />
        </div>
      </div>
    </section>
  );
};
